import React from "react";
import { FilledInfoIcon } from "icons/payments/FilledInfoIcon";
import Skeleton from "react-loading-skeleton";
import { formatCurrency } from "utils/HelperFunctions";
import { usePortalSettingsData } from "store/slices/dashboardSlice";

interface ConvenienceFeeDisplayProps {
  conFee: number;
  isFeeLoading: boolean;
  isPaymentLink: boolean;
  setShowPreview: (value: boolean) => void;
  customClassName?: string;
  isStaxGateway?: boolean;
  isTokenAvailable?: boolean;
}

export const ConvenienceFeeDisplay: React.FC<ConvenienceFeeDisplayProps> = ({
  conFee,
  isFeeLoading,
  isPaymentLink,
  setShowPreview,
  customClassName = "mt-3",
  isStaxGateway = false,
  isTokenAvailable = true,
}) => {
  const portalSettings = usePortalSettingsData();
  return (
    <div
      className={`flex items-start gap-2 text-xs font-normal text-txtParagraph ${customClassName}`}
    >
      <span className="shrink-0">
        <FilledInfoIcon />
      </span>
      {isPaymentLink ? (
        !isTokenAvailable ? (
          <p>
            A{" "}
            <span className="lowercase">
              {isStaxGateway
                ? "surcharge"
                : portalSettings?.cc_fee_label
                  ? portalSettings?.cc_fee_label
                  : "convenience fee"}
            </span>{" "}
            will be applied to your transaction.
          </p>
        ) : (
          <p>
            A{" "}
            <span className="lowercase">
              {isStaxGateway
                ? "surcharge"
                : portalSettings?.cc_fee_label
                  ? portalSettings?.cc_fee_label
                  : "convenience fee"}
            </span>{" "}
            of{" "}
            {isFeeLoading ? (
              <span className="inline-block align-middle">
                <Skeleton width={35} height={11} />
              </span>
            ) : (
              formatCurrency(conFee) || "$0.00"
            )}{" "}
            will be applied to your transaction.
          </p>
        )
      ) : (
        <div>
          {!isTokenAvailable ? (
            <p>
              A{" "}
              <span className="lowercase">
                {isStaxGateway
                  ? "surcharge"
                  : portalSettings?.cc_fee_label
                    ? portalSettings?.cc_fee_label
                    : "convenience fee"}
              </span>{" "}
              will be added to each payment.
            </p>
          ) : (
            <p>
              A{" "}
              <span
                className="text-blue underline cursor-pointer hover:!text-btnPrimaryHover"
                onClick={() => {
                  setShowPreview(true);
                }}
              >
                <span className="lowercase">
                  {isStaxGateway
                    ? "surcharge"
                    : portalSettings?.cc_fee_label
                      ? portalSettings?.cc_fee_label
                      : "convenience fee"}
                </span>
              </span>{" "}
              is added to each payment.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

interface ShowMoreButtonProps {
  currentCount: number;
  totalCount: number;
  onToggle: () => void;
}

export const ShowMoreButton: React.FC<ShowMoreButtonProps> = ({
  currentCount,
  totalCount,
  onToggle,
}) => {
  const isShowingAll = currentCount > totalCount;

  return (
    <div
      className="w-max cursor-pointer text-btnPrimaryActive hover:!text-btnPrimaryHover font-medium text-sm"
      onClick={onToggle}
    >
      {isShowingAll ? "Show less" : "Show more"}
    </div>
  );
};

interface LoadingSkeletonsProps {
  count?: number;
}

export const PaymentMethodLoadingSkeletons: React.FC<LoadingSkeletonsProps> = ({
  count = 3,
}) => {
  return (
    <div className="flex flex-col border-b-gray border-b pb-2 gap-y-2">
      {Array.from({ length: count }).map((_, index) => (
        <Skeleton key={index} height={45} baseColor="var(--color-bg-third)" />
      ))}
    </div>
  );
};
